import React, { useState, useCallback } from 'react'
import LoaderBase from 'react-loader-spinner'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'

import Alert from 'components/Alert'
import Modal, { Title } from 'components/Dialogs/Modal'
import Hr from 'components/HorizontalRuler'
import UnstyledLink from 'components/UnstyledLink'
import Button from 'components/Button'
import { File } from 'components/Input'
import { uploadRFP, clearUploadErrors } from 'features/carrierProposal/actions'
import {
  isUploadingSelector,
  uploadErrorSelector,
} from 'features/carrierProposal/selectors'

import { ALLOWED_FILETYPES } from '../../../constants'

const StyledAlert = styled(Alert)`
  margin-top: 10px;
`
const StyledFile = styled(File)`
  background: transparent;
  height: 255px;
  width: calc(100% - ${({ value }) => (value ? 4 : 16)}px);
  font-weight: 400;
`
const ActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 20px;

  > :first-child {
    margin-bottom: 15px;
  }

  ${Button}:disabled {
    opacity: 0.4;
  }
`
const Loader = styled(LoaderBase)`
  display: flex;
  justify-content: center;
  align-items: center;
`
const ErrorMessage = styled.span`
  max-width: 505px;
  text-align: left;
`

export default function UploadDialog({
  isOpen,
  closeModal,
  downloadURL,
  linkUUID,
}: {
  isOpen: boolean
  closeModal: () => void
  downloadURL: string
  linkUUID: string
}) {
  const dispatch = useDispatch()
  const [file, setFile] = useState<File | undefined>(undefined)
  const onChangeFile = useCallback(
    (value: File | undefined) => {
      dispatch(clearUploadErrors())
      setFile(value)
    },
    [dispatch]
  )

  const isUploading = useSelector(isUploadingSelector)
  const errors = useSelector(uploadErrorSelector)
  const submit = useCallback(() => {
    if (file) {
      dispatch(
        uploadRFP(linkUUID, file, () => {
          closeModal()
          setFile(undefined)
        })
      )
    }
  }, [linkUUID, file, dispatch, closeModal])

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Upload prices"
      onRequestClose={closeModal}
      modalWidth={565}
    >
      <Title>Upload Prices</Title>
      <Hr />
      <StyledAlert type="info">
        <b>
          <UnstyledLink href={downloadURL}>Click here</UnstyledLink>
        </b>{' '}
        &nbsp; to download a template of the file.
      </StyledAlert>
      {errors && (
        <StyledAlert type="error">
          <ErrorMessage>
            <b>Failed to upload file.</b>
            &nbsp;
            {errors}
          </ErrorMessage>
        </StyledAlert>
      )}
      <StyledFile
        name="proposalFile"
        onChange={onChangeFile}
        value={file}
        accept={ALLOWED_FILETYPES}
        error={false}
      />
      <ActionWrapper>
        <Button
          variant="main"
          disabled={!file || isUploading || !!errors}
          onClick={submit}
        >
          {isUploading ? (
            <Loader type="TailSpin" width={20} height={20} />
          ) : (
            'Upload File'
          )}
        </Button>
        <Button variant="secondary" onClick={closeModal} disabled={isUploading}>
          Cancel
        </Button>
      </ActionWrapper>
    </Modal>
  )
}
